import React, { useState } from "react";
import { motion } from "framer-motion";
import Spline from '@splinetool/react-spline';
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import DropdownNav from "./DropdownNav";
import BlinkingCursor from "./BlinkingCursor";
import ContactModal from "./ContactModal";
// Removed terminal navigation components
import { 
  Cpu, 
  Shield, 
  Network, 
  Zap, 
  Globe, 
  Lock, 
  Eye, 
  Code, 
  ArrowRight, 
  Mail,
  MapPin,
  Users,
  Target,
  Layers,
  ChevronDown,
  Menu,
  X
} from "lucide-react";

const GodquantumLanding = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Google Form URL - Replace with actual form URL
    window.open('https://forms.google.com/your-form-url', '_blank');
    setSubmitted(true);
  };

  // Professional navigation structure inspired by konghq.com
  const navItems = [
    {
      id: "company",
      label: "Company",
      dropdown: [
        { label: "About Us", href: "#about" },
        { label: "Vision", href: "#vision" },
        { label: "Why Godquantum?", href: "#about" },
        { label: "Careers", href: "https://forms.gle/85WmvhX9xBAtUiJH7", external: true },
        { label: "Newsletter", href: "https://dyota.substack.com", external: true },
        { label: "Investors", href: "https://forms.gle/ujBE7meWTSj9tjnm7", external: true },
        { label: "Contact Us", href: "https://forms.gle/xy3W8pUjroDPVasx7", external: true }
      ]
    },
    {
      id: "product",
      label: "Product",
      dropdown: [
        {
          label: "Software",
          subitems: [
            { label: "Control System", href: "#control-system" },
            { label: "GUI", href: "#gui" },
            { label: "APIs", href: "#apis" }
          ]
        },
        {
          label: "Hardware", 
          subitems: [
            { label: "QubitPulsing™ - Pulse Processing Unit", href: "#qubitpulsing" },
            { label: "QubitFabrica™ - CPU/GPU/QPU Scheduler", href: "#qubitfabrica" }
          ]
        }
      ]
    },
    {
      id: "platform",
      label: "Platform",
      dropdown: [
        { label: "QubitHub™ - Hyperscaler Global DCaaS", href: "#quantum-platform" },
        { label: "QubitCali™ - Superconducting Qubit Calibration Suite", href: "#quantum-platform" },
        { label: "QubitDeviG™ - Device Gateway", href: "#quantum-platform" },
        { label: "QubitEngine™ - Core Engine", href: "#quantum-platform" },
        { label: "QubitTrans™ - Polymath LangChain Framework", href: "#quantum-platform" }
      ]
    },
    {
      id: "technology",
      label: "Technology",
      dropdown: [
        { label: "Applications", href: "#where-quantum-matters" },
        { label: "Use Cases", href: "#use-cases" }
      ]
    },
    { id: "partners", label: "Partners", href: "#partners" },
    {
      id: "support",
      label: "Support",
      dropdown: [
        { label: "FAQs", href: "#faq" },
        { label: "Community", href: "https://discord.gg/aNKPwwBy" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white selection:bg-emerald-400/30 selection:text-white overflow-x-hidden">
      {/* Top Notice Bar */}
      <div className="bg-gradient-to-r from-emerald-500/10 via-cyan-500/10 to-indigo-500/10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 py-2 text-center text-sm">
          <span className="inline-flex items-center gap-2">
            🇮🇳 <span className="font-medium text-emerald-300">Made in India</span> — 
            Building the world's first commercially available 
            <span className="font-medium text-white">Quantum Operating System</span>
          </span>
        </div>
      </div>

      {/* Enhanced Navigation Header */}
      <header className="dark-header backdrop-blur supports-[backdrop-filter]:bg-black/50">
        <div className="flex items-center w-full max-w-7xl mx-auto">
          {/* Logo Section - Left Aligned */}
          <div className="flex-shrink-0 mr-auto">
            <a href="#top" className="flex items-center group">
              <div className="logo-container relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_a1f0c07e-de70-48cd-86ab-b2b31bd62f8b/artifacts/w8bj5ad5_godquantum_logo_typograph.png"
                  alt="Godquantum Systems - Adding Qubits to Life"
                  className="logo-magnifier h-20 sm:h-18 md:h-20 lg:h-24 w-auto object-contain filter brightness-110 contrast-125 saturate-110"
                  title="Hover to read tagline: Adding Qubits to Life"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
            </a>
          </div>
          
          {/* Navigation and CTA - Right Aligned with proper spacing */}
          <div className="flex items-center space-x-8 ml-16">
            <DropdownNav navItems={navItems} />
            <div className="hidden lg:block">
              <Button 
                className="btn-primary px-6 py-3"
                onClick={() => setIsContactModalOpen(true)}
              >
                Contact Us
              </Button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2 z-50 relative"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Toggle menu"
          >
            {showMobileMenu ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>
        </div>

        {/* Enhanced Mobile Menu */}
        {showMobileMenu && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden fixed inset-x-0 top-24 bg-black/95 backdrop-blur border-t border-white/10 z-40 max-h-[80vh] overflow-y-auto"
          >
            <div className="px-4 py-6 space-y-6">
              {navItems.map((item) => (
                <div key={item.id} className="space-y-2">
                  <div className="text-emerald-300 font-medium text-lg">{item.label}</div>
                  {item.dropdown ? (
                    <div className="pl-4 space-y-2">
                      {item.dropdown.map((subItem, index) => (
                        <div key={index}>
                          {subItem.subitems ? (
                            <div className="space-y-1">
                              <div className="text-gray-400 font-medium">{subItem.label}</div>
                              <div className="pl-4 space-y-1">
                                {subItem.subitems.map((subSubItem, subIndex) => (
                                  <a
                                    key={subIndex}
                                    href={subSubItem.href}
                                    className="block py-1 text-gray-500 hover:text-emerald-300 transition-colors text-sm"
                                    onClick={() => setShowMobileMenu(false)}
                                  >
                                    {subSubItem.label}
                                  </a>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <a
                              href={subItem.href}
                              className="block py-1 text-gray-400 hover:text-emerald-300 transition-colors"
                              onClick={() => setShowMobileMenu(false)}
                            >
                              {subItem.label}
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <a
                      href={item.href}
                      className="block py-1 text-gray-400 hover:text-emerald-300 transition-colors"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      {item.label}
                    </a>
                  )}
                </div>
              ))}
              <Button 
                className="w-full btn-primary mt-6"
                onClick={() => {
                  setShowMobileMenu(false);
                  window.open('https://forms.gle/oLirQqXxYpqUh6Fp6', '_blank');
                }}
              >
                Request Access
              </Button>
            </div>
          </motion.div>
        )}
      </header>

      {/* Removed terminal navigation for cleaner design */}

      {/* Hero Section with Split Layout */}
      <section id="hero" className="relative min-h-screen pt-32">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(0,255,209,0.15),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(16,185,129,0.1),transparent_50%)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
            {/* Left Column - Content */}
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <Badge className="mb-6 bg-emerald-500/10 text-emerald-300 border-emerald-500/20">
                  Stealth Mode • India NQM Aligned
                </Badge>
                
                <h1 className="display-huge leading-none mb-6 font-mono">
                  Full‑Stack Fault‑Tolerant{" "}
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-300">
                    <BlinkingCursor 
                      text="Quantum Computing"
                      className="inline"
                    />
                  </span>
                </h1>
                
                <p className="body-large text-gray-300 max-w-xl leading-relaxed">
                  Bridging classical, graphical, and QPU compute into a production‑grade 
                  <span className="text-emerald-300 font-semibold"> Quantum OS</span>—
                  for quantum internet, data centres, and smart power grids.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                className="flex flex-wrap gap-4"
              >
                <Button 
                  className="btn-primary dark-button-animate group"
                  onClick={() => window.open('https://forms.gle/oLirQqXxYpqUh6Fp6', '_blank')}
                >
                  Request Early Access
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                
                <Button 
                  className="btn-secondary"
                  onClick={() => document.getElementById('platform').scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Quantum OS
                </Button>
              </motion.div>

              {/* Removed company info line */}
            </div>

            {/* Right Column - 3D Spline Integration */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
              className="relative"
            >
              <div className="relative w-full h-[600px] overflow-visible rounded-3xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent">
                <Spline
                  scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode"
                  style={{ 
                    width: "100%", 
                    height: "100%",
                    borderRadius: "24px"
                  }}
                />
                
                {/* Terminal-Style Overlay */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="terminal-window">
                    <div className="terminal-header">
                      <div className="terminal-dots">
                        <div className="terminal-dot red"></div>
                        <div className="terminal-dot yellow"></div>
                        <div className="terminal-dot green"></div>
                      </div>
                      <span className="text-gray-400 text-xs ml-2">quantum-os-core@godquantum:~</span>
                    </div>
                    <div className="terminal-output">
                      <div className="command-prompt text-emerald-400 mb-2">systemctl status quantum-os</div>
                      <div className="text-emerald-300 mb-1">● quantum-os.service - Quantum OS Kernel</div>
                      <div className="text-gray-300 mb-2 text-xs">   Active: active (running)</div>
                      
                      <div className="grid grid-cols-3 gap-3 mt-3 text-center text-xs">
                        {[
                          ["99.99%", "QoS target"],
                          ["<1ms", "Control latency"], 
                          ["NDA", "Design partner"]
                        ].map(([value, label]) => (
                          <div key={value} className="bg-black/40 rounded p-2 border border-emerald-500/20">
                            <div className="text-emerald-300 font-mono font-semibold">{value}</div>
                            <div className="text-gray-400 text-xs">{label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-gray-400"
          >
            <ChevronDown className="w-6 h-6" />
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section id="vision" className="py-24 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="display-medium mb-6">Mission & Vision</h2>
            <p className="body-large text-gray-300 max-w-4xl mx-auto">
              Redefining technological and economic sovereignty through a Full‑Stack Quantum ecosystem—
              spanning Communication, Sensing, Devices, Networking, Cryptography & Security, and the Quantum OS.
              <span className="text-emerald-300"> Built in India, for the world.</span>
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Shield className="w-8 h-8" />,
                title: "Sovereign Compute",
                description: "Control planes and toolchains designed for national missions, defence‑grade hardening, and export‑ready commercial use."
              },
              {
                icon: <Layers className="w-8 h-8" />,
                title: "Industry at Depth", 
                description: "Material science, chemicals, drug discovery, energy, telecom, finance, automotive, semicon, aerospace, logistics."
              },
              {
                icon: <Zap className="w-8 h-8" />,
                title: "Fault‑Tolerance First",
                description: "Architected for error correction readiness, hybrid orchestration, and verifiable outcomes across qubits and classical fabric."
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <Card className="p-8 bg-black/40 border-white/10 dark-hover dark-transition h-full">
                  <div className="text-emerald-400 mb-4">{item.icon}</div>
                  <h3 className="heading-2 mb-4 text-white">{item.title}</h3>
                  <p className="body-medium text-gray-300">{item.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quantum OS Deep Dive */}
      <section id="quantumos" className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="display-medium">Quantum OS — Architecture</h2>
              <p className="body-large text-gray-300 mt-4">Production-grade quantum computing infrastructure</p>
            </motion.div>
            <Badge className="bg-emerald-400/10 text-emerald-300 border-emerald-400/30 mt-4 md:mt-0">
              Design‑Partner Access
            </Badge>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <Cpu className="w-6 h-6" />,
                title: "Control Plane",
                description: "Deterministic timing, pulse‑level control, circuit schedulers, and routing across QPUs."
              },
              {
                icon: <Code className="w-6 h-6" />,
                title: "Kernel & Drivers", 
                description: "Abstractions for qubits, couplers, resonators; vendor‑agnostic HAL and FFI."
              },
              {
                icon: <Lock className="w-6 h-6" />,
                title: "Security",
                description: "Post‑quantum cryptography, HSM integration, and secure enclaves for workload isolation."
              },
              {
                icon: <Network className="w-6 h-6" />,
                title: "Networking",
                description: "Quantum repeaters, entanglement management, and classical back‑channels for orchestration."
              },
              {
                icon: <Eye className="w-6 h-6" />,
                title: "Observability",
                description: "Shot‑level telemetry, qubit health, error syndromes, and correlated traces to classical spans."
              },
              {
                icon: <Globe className="w-6 h-6" />,
                title: "SDK & Tooling",
                description: "Compilers, optimizers (Grover, QAOA, VQE), and domain templates for rapid prototyping."
              }
            ].map((component, index) => (
              <motion.div
                key={component.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-6 bg-white/[0.02] border-white/10 dark-hover dark-transition h-full">
                  <div className="text-emerald-400 mb-3">{component.icon}</div>
                  <h3 className="heading-3 mb-3 text-white">{component.title}</h3>
                  <p className="body-small text-gray-300">{component.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Section - Target for "Explore Quantum OS" */}
      <section id="platform" className="py-24 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-6 bg-emerald-500/10 text-emerald-300 border-emerald-500/20">
              Quantum Platform
            </Badge>
            <h2 className="display-medium mb-6">Quantum OS Platform Suite</h2>
            <p className="body-large text-gray-300 max-w-4xl mx-auto">
              Complete quantum computing infrastructure with unified platform engineering approach
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "QubitHub™",
                subtitle: "Hyperscaler Global DCaaS",
                description: "Global quantum data center as a service with fault-tolerant infrastructure",
                icon: <Globe className="w-6 h-6" />
              },
              {
                title: "QubitEngine™", 
                subtitle: "Core Quantum Engine",
                description: "High-performance quantum processing core with optimized execution pathways",
                icon: <Cpu className="w-6 h-6" />
              },
              {
                title: "QubitCali™",
                subtitle: "Superconducting Qubit Calibration Suite", 
                description: "Precision calibration and characterization for quantum devices",
                icon: <Zap className="w-6 h-6" />
              },
              {
                title: "QubitDeviG™",
                subtitle: "Device Gateway",
                description: "Unified device gateway for quantum hardware abstraction and control",
                icon: <Network className="w-6 h-6" />
              },
              {
                title: "QubitTrans™", 
                subtitle: "Polymath LangChain Framework",
                description: "Advanced language processing framework for quantum algorithm development",
                icon: <Code className="w-6 h-6" />
              },
              {
                title: "QubitShield™",
                subtitle: "Security & Encryption",
                description: "Post-quantum cryptography and secure quantum communication protocols",
                icon: <Shield className="w-6 h-6" />
              }
            ].map((platform, index) => (
              <motion.div
                key={platform.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-6 bg-black/40 border-white/10 dark-hover dark-transition h-full">
                  <div className="text-emerald-400 mb-4">{platform.icon}</div>
                  <h3 className="heading-3 mb-2 text-white">{platform.title}</h3>
                  <p className="text-emerald-300 text-sm mb-3 font-medium">{platform.subtitle}</p>
                  <p className="body-small text-gray-300">{platform.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA Section - Industry Standard */}
      <section className="py-16 bg-gradient-to-r from-emerald-900/20 to-blue-900/20 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-2 mb-4 text-white">Ready to Transform Your Quantum Computing?</h2>
            <p className="body-large text-gray-300 mb-8 max-w-2xl mx-auto">
              Connect with our quantum computing experts and discover how Godquantum Systems can accelerate your research and development.
            </p>
            <Button 
              className="btn-primary px-8 py-4 text-lg"
              onClick={() => window.open('https://forms.gle/oLirQqXxYpqUh6Fp6', '_blank')}
            >
              Get In Touch
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </div>
  );
};

export default GodquantumLanding;